import { useEffect } from 'react';

import { useQueries } from '@tanstack/react-query';

import { getGetApiArbitrageAvenantQueryOptions } from '@/api/arbitrage/arbitrage';
import { useQps } from '@/hooks/QpsContext';
import { log } from '@/utils/functions/logger/logger';
import { parseQpsQuestionnaire } from '@/utils/functions/qps/parseQpsQuestionnaire';

import type { ArbitrageInvestissableItem } from '@/api/placementApi.schemas';
import type { QpsEntry } from '@/types/qps';

type QpsUnavailable = {
  productName: string;
  reason: string;
};

// Supports that carry a questionnaire (`null` means none), in API order. A single unusable
// questionnaire makes the whole arbitrage unavailable: skipping it would get it rejected by Spirica.
const resolveQpsEntries = (supportsInvestis: ArbitrageInvestissableItem[] = []) => {
  const entries: QpsEntry[] = [];
  let unavailable: QpsUnavailable | null = null;

  for (const support of supportsInvestis) {
    if (support.questionnaire == null) continue;
    const productName = support.label ?? '';
    const parsed = parseQpsQuestionnaire(support.questionnaire);

    if (parsed.status === 'valid' && support.codeSupport) {
      entries.push({
        codeSupport: support.codeSupport,
        questionnaire: parsed.questionnaire,
        productName,
        productIsin: support.codeIsin ?? '',
        description: support.questionnaireInfos?.description ?? null,
        avenantReference: support.avenants?.[0]?.reference ?? null,
      });
    } else {
      unavailable ??= {
        productName,
        reason: parsed.status === 'invalid' ? parsed.reason : 'QPS support has no codeSupport',
      };
    }
  }

  return { entries, unavailable };
};

/**
 * Questionnaires to pass before accepting the arbitrage. A questionnaire counts as passed only when
 * the QPS context holds this very arbitrage, in the same version.
 */
const useArbitrageQps = (arbitrageId: string, supportsInvestis?: ArbitrageInvestissableItem[]) => {
  const { arbitrageId: contextArbitrageId, questionnaires, initQuestionnaires } = useQps();
  const { entries, unavailable } = resolveQpsEntries(supportsInvestis);

  useEffect(() => {
    if (unavailable) log.error(`[qps] ${unavailable.reason}`);
  }, [unavailable?.reason]);

  const isPassed = (entry: QpsEntry) =>
    contextArbitrageId === arbitrageId &&
    questionnaires.some(
      (questionnaire) =>
        questionnaire.codeSupport === entry.codeSupport &&
        questionnaire.version === entry.questionnaire.version &&
        questionnaire.isPassed
    );

  // Avenants of the questionnaires still to pass, loaded from the summary: the call takes about
  // 1.5 s, the link is then ready as soon as the question shows. Kept in cache while the summary
  // (and the questionnaires it hosts) is mounted.
  useQueries({
    queries: entries
      .filter((entry) => !isPassed(entry))
      .flatMap(({ codeSupport, avenantReference }) =>
        avenantReference
          ? [getGetApiArbitrageAvenantQueryOptions(arbitrageId, { codeSupport, reference: avenantReference })]
          : []
      ),
  });

  return {
    entries,
    unavailableProductName: unavailable?.productName ?? null,
    // Something to do before accepting: an unusable questionnaire, or one not passed yet.
    isRequired: unavailable !== null || entries.some((entry) => !isPassed(entry)),
    // Hands the questionnaires to the context; returns the codes already passed (skipped, but still
    // counted in « Questionnaire n sur total »).
    start: () =>
      initQuestionnaires(arbitrageId, entries)
        .filter((questionnaire) => questionnaire.isPassed)
        .map((questionnaire) => questionnaire.codeSupport),
  };
};

export default useArbitrageQps;
